import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { formatDate } from '@xdocs/i18n';
import { Badge, Button, EmptyState, Modal, Skeleton, cn, useI18n } from '@xdocs/ui';
import { safeT } from '../../../i18n/safeT.js';
import { PromoCodeField } from './PromoCodeField.js';
import { useCheckoutStart } from './useCheckoutStart.js';
import { usePromoRedeem } from './usePromoRedeem.js';
import { useSubscription } from './useSubscription.js';
import { useSettingsBillingT } from './useSettingsBillingT.js';
import styles from './PlanCard.module.css';

/** F20/F24 plan card + F46 cancel flow (consequence copy, not a generic confirm). */
export function PlanCard(): ReactElement {
  const { locale } = useI18n();
  const { t } = useSettingsBillingT();
  const { subscription, isLoading, isError, onRetry, cancel, isCanceling } = useSubscription();
  const checkout = useCheckoutStart();
  const promo = usePromoRedeem('subscription');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | undefined>(undefined);

  async function handleCancel(): Promise<void> {
    setCancelError(undefined);
    try {
      await cancel({ atPeriodEnd: true });
      setCancelOpen(false);
    } catch {
      setCancelError(t('settingsBilling:cancel.error'));
    }
  }

  if (isLoading) {
    return (
      <section aria-busy="true" className={cn(styles['card'])}>
        <Skeleton shape="text-line" width="40%" />
        <Skeleton shape="text-line" width="60%" />
      </section>
    );
  }

  if (isError) {
    return (
      <EmptyState kind="error" title={t('settingsBilling:plan.status.error.title')} action={{ label: t('settingsBilling:action.retry'), onClick: onRetry }} />
    );
  }

  const isPro = subscription !== null && subscription !== undefined && (subscription.status === 'active' || subscription.status === 'trialing');
  const isPastDue = subscription?.status === 'past_due';
  const isCancelingAtPeriodEnd = Boolean(subscription?.cancelAt);

  return (
    <section className={cn(styles['card'])}>
      <h2 className={cn(styles['title'])}>{t('settingsBilling:plan.title')}</h2>

      {isPastDue && (
        <div role="alert" className={cn(styles['pastDueBanner'])}>
          <Badge tone="error">{t('settingsBilling:plan.pastDue.title')}</Badge>
          <p>
            {subscription?.currentPeriodEnd
              ? t('settingsBilling:plan.pastDue.description', { date: formatDate(locale, subscription.currentPeriodEnd, { dateStyle: 'medium' }) })
              : t('settingsBilling:plan.pastDue.descriptionNoDate')}
          </p>
          <Link to="/checkout/error?reason=past_due">
            <Button variant="primary" size="sm">
              {t('settingsBilling:plan.action.updatePayment')}
            </Button>
          </Link>
        </div>
      )}

      {!isPastDue && isPro && (
        <>
          <p className={cn(styles['planLine'])}>
            <Badge tone="pro">{t('settingsBilling:plan.pro.title')}</Badge>
            {isCancelingAtPeriodEnd && subscription?.currentPeriodEnd
              ? ` ${t('settingsBilling:plan.pro.cancelsAt', { date: formatDate(locale, subscription.currentPeriodEnd, { dateStyle: 'medium' }) })}`
              : subscription?.currentPeriodEnd
                ? ` ${t('settingsBilling:plan.pro.renews', { date: formatDate(locale, subscription.currentPeriodEnd, { dateStyle: 'medium' }) })}`
                : null}
          </p>
          {!isCancelingAtPeriodEnd && (
            <div className={cn(styles['actions'])}>
              <Button variant="ghost" onClick={() => setCancelOpen(true)}>
                {t('settingsBilling:plan.action.cancel')}
              </Button>
            </div>
          )}
        </>
      )}

      {!isPastDue && !isPro && (
        <>
          <p className={cn(styles['planLine'])}>{t('settingsBilling:plan.free.title')}</p>
          <PromoCodeField promo={promo} />
          <div className={cn(styles['actions'])}>
            <Button
              variant="primary"
              state={checkout.isStarting ? 'loading' : 'idle'}
              onClick={() => void checkout.start('pro_subscription', promo.status === 'applied' ? { promoCode: promo.code } : undefined)}
            >
              {t('settingsBilling:plan.action.upgrade')}
            </Button>
          </div>
          {checkout.errorMessage && (
            <p role="alert" className={cn(styles['error'])}>
              {safeT(locale, checkout.errorMessage)}
            </p>
          )}
        </>
      )}

      <Modal
        open={cancelOpen}
        onClose={() => !isCanceling && setCancelOpen(false)}
        title={t('settingsBilling:cancel.title')}
        size="sm"
        footer={
          <div className={cn(styles['cancelActions'])}>
            <Button variant="secondary" onClick={() => setCancelOpen(false)} disabled={isCanceling}>
              {t('settingsBilling:cancel.keepPro')}
            </Button>
            <Button variant="destructive" state={isCanceling ? 'loading' : 'idle'} onClick={() => void handleCancel()}>
              {t('settingsBilling:cancel.confirm')}
            </Button>
          </div>
        }
      >
        <p className={cn(styles['cancelBody'])}>
          {subscription?.currentPeriodEnd
            ? t('settingsBilling:cancel.body', { date: formatDate(locale, subscription.currentPeriodEnd, { dateStyle: 'long' }) })
            : t('settingsBilling:cancel.bodyNoDate')}
        </p>
        {cancelError && (
          <p role="alert" className={cn(styles['error'])}>
            {cancelError}
          </p>
        )}
      </Modal>
    </section>
  );
}
